import {
  MapPin,
  Presentation,
  Users,
  Video,
  Coffee,
  UtensilsCrossed,
  Wine,
  type LucideIcon,
} from 'lucide-react';

import type { AgendaItemType } from '../data/types';
import { getSpeakersByIds, type SpeakerCompany } from '../data/speakers';
import { getLocationById } from '../data/locations';
import { useEvent } from '../contexts/EventContext';
import { SpeakerAvatar } from '../components/SpeakerAvatar';
import { ExternalLinkButton } from '../components/ExternalLinkButton';

interface TypeStyle {
  container: string;
  icon: string;
  Icon: LucideIcon;
}

// Agenda item types map onto the themed token families (primary/accent/secondary/
// success) so a year's palette recolors the icons. Class strings are written in full
// because Tailwind only emits utilities it can find literally in the source.
const TYPE_STYLES: Record<AgendaItemType, TypeStyle> = {
  presentation: {
    container: 'bg-primary-100 dark:bg-primary-900',
    icon: 'text-primary-600 dark:text-primary-400',
    Icon: Presentation,
  },
  panel: {
    container: 'bg-secondary-100 dark:bg-secondary-900',
    icon: 'text-secondary-600 dark:text-secondary-400',
    Icon: Users,
  },
  video: {
    container: 'bg-accent-100 dark:bg-accent-900',
    icon: 'text-accent-600 dark:text-accent-400',
    Icon: Video,
  },
  break: {
    container: 'bg-accent-100 dark:bg-accent-900',
    icon: 'text-accent-600 dark:text-accent-400',
    Icon: Coffee,
  },
  meal: {
    container: 'bg-success-100 dark:bg-success-900',
    icon: 'text-success-600 dark:text-success-400',
    Icon: UtensilsCrossed,
  },
  reception: {
    container: 'bg-primary-100 dark:bg-primary-900',
    icon: 'text-primary-600 dark:text-primary-400',
    Icon: Wine,
  },
};

const getTypeIcon = (type: AgendaItemType) => {
  const iconClasses = 'h-5 w-5 sm:h-6 sm:w-6';
  const containerClasses =
    'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12';
  const style = TYPE_STYLES[type];
  if (!style) return null;
  const { Icon } = style;
  return (
    <div className={`${containerClasses} ${style.container}`}>
      <Icon className={`${iconClasses} ${style.icon}`} />
    </div>
  );
};

// Swap to the dark-background variant in dark mode. A logo without one sits on a
// white chip in dark mode only, rather than being recoloured.
function CompanyLogo({ company }: { company: SpeakerCompany }) {
  const size = 'h-6 w-auto';
  if (!company.logoUrlDark) {
    return (
      <img
        src={company.logoUrl}
        alt={company.name}
        className={`${size} dark:box-content dark:rounded dark:bg-white dark:px-2 dark:py-1`}
      />
    );
  }
  return (
    <>
      <img
        src={company.logoUrl}
        alt={company.name}
        className={`${size} dark:hidden`}
      />
      <img
        src={company.logoUrlDark}
        alt={company.name}
        className={`${size} hidden dark:block`}
      />
    </>
  );
}

export function AgendaPage() {
  const event = useEvent();

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl dark:text-white">
          Meeting Agenda
        </h1>
        <p className="mt-2 text-base text-gray-600 sm:text-lg dark:text-gray-300">
          Schedule and topics for the Annual General Meeting
        </p>
      </div>

      {event.agendaNotes && event.agendaNotes.length > 0 && (
        <div className="bg-secondary-50 dark:bg-secondary-900/20 mb-8 rounded-lg p-4 sm:p-6">
          <h3 className="text-secondary-900 dark:text-secondary-300 text-base font-semibold sm:text-lg">
            Important Information
          </h3>
          <ul className="text-secondary-800 dark:text-secondary-300 mt-3 list-inside list-disc space-y-2 text-sm sm:text-base">
            {event.agendaNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {event.agenda.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center sm:p-12 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl dark:text-white">
            Agenda to be announced
          </h2>
          <p className="mx-auto mt-2 max-w-prose text-sm text-gray-600 sm:text-base dark:text-gray-300">
            The {event.meta.year} agenda is being finalized and will be
            published here closer to the event. Please check back soon.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {event.agenda.map((day, dayIndex) => (
            <div key={dayIndex}>
              <div className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3">
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl dark:text-white">
                  {day.date}
                </h2>
                <span className="bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300 rounded-full px-3 py-1 text-sm font-medium">
                  Day {dayIndex + 1}
                </span>
              </div>
              <div className="space-y-4">
                {day.items.map((item, index) => {
                  const speakers = item.speakerIds
                    ? getSpeakersByIds(item.speakerIds)
                    : [];
                  const location = getLocationById(event, item.locationId);
                  return (
                    <div
                      key={index}
                      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-6 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <div className="flex items-start gap-3">
                        {getTypeIcon(item.type)}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <span className="bg-secondary-100 text-secondary-800 dark:bg-secondary-900 dark:text-secondary-300 inline-flex items-center rounded-full px-3 py-1 text-sm font-medium">
                                  {item.time}
                                </span>
                                {location && (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 sm:text-sm dark:text-gray-400">
                                    <MapPin className="h-4 w-4" />
                                    {location.name}
                                    {location.floor && ` - ${location.floor}`}
                                  </span>
                                )}
                              </div>
                              <h3 className="mt-3 text-lg font-semibold text-gray-900 sm:text-xl dark:text-white">
                                {item.title}
                              </h3>
                              {speakers.length > 0 && (
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                  {speakers.map((s, i) => (
                                    <span key={s.id}>
                                      {i > 0 && ', '}
                                      {s.linkedinUrl ? (
                                        <a
                                          href={s.linkedinUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="hover:text-primary-600 dark:hover:text-primary-400 underline-offset-2 hover:underline"
                                        >
                                          {s.name}
                                        </a>
                                      ) : (
                                        s.name
                                      )}
                                    </span>
                                  ))}
                                </p>
                              )}
                              <p className="mt-2 text-sm text-gray-600 sm:text-base dark:text-gray-300">
                                {item.description}
                              </p>
                              {item.link && (
                                <ExternalLinkButton link={item.link} />
                              )}
                              {speakers.some((s) => s.company) && (
                                <div className="mt-3 flex flex-wrap items-center gap-4">
                                  {speakers.map(
                                    (s) =>
                                      s.company && (
                                        <CompanyLogo
                                          key={s.id}
                                          company={s.company}
                                        />
                                      ),
                                  )}
                                </div>
                              )}
                            </div>
                            {speakers.length > 0 && (
                              <div className="flex gap-2">
                                {speakers.map((speaker, speakerIndex) => (
                                  <SpeakerAvatar
                                    key={speakerIndex}
                                    name={speaker.name}
                                    picture={speaker.picture}
                                    size="sm"
                                    className="sm:h-16 sm:w-16"
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
